// Dashboard header — ad-hoc SMS composer for admins/supervisors with
// sms_access. Templates for common texts + recent-sends history inside.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { MessageSquarePlus, Send, History, BookmarkPlus } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SMS_ROLES = new Set(['master_admin', 'admin', 'manager', 'supervisor', 'domain_admin', 'domain_supervisor']);
const MAX_LEN = 459;

const fmtWhen = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

export const SendSmsButton = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [savingTpl, setSavingTpl] = useState(false);
  const [tplName, setTplName] = useState('');
  const [namingTpl, setNamingTpl] = useState(false);
  const [match, setMatch] = useState(null);

  const loadExtras = useCallback(() => {
    axios.get(`${API}/analytics/sms-templates`)
      .then((r) => setTemplates(r.data.templates || [])).catch(() => {});
    axios.get(`${API}/analytics/sms/direct-history`)
      .then((r) => setHistory(r.data.rows || [])).catch(() => {});
  }, []);

  useEffect(() => { if (open) loadExtras(); }, [open, loadExtras]);

  useEffect(() => {
    if (!open || phone.replace(/\D/g, '').length < 8) { setMatch(null); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API}/analytics/sms/contact-match`, { params: { phone } })
        .then((r) => setMatch(r.data.found || r.data.club ? r.data : null))
        .catch(() => setMatch(null));
    }, 500);
    return () => clearTimeout(t);
  }, [phone, open]);

  if (!user || !SMS_ROLES.has(user.role)) return null;
  const canSend = !!user.sms_access;

  const fillPlaceholders = (text) => text
    .replace(/\{first_name\}/gi, match?.first_name || '{first_name}')
    .replace(/\{club\}/gi, match?.club || '{club}');

  const send = async () => {
    if (!phone.trim() || !message.trim() || sending) return;
    const finalMsg = fillPlaceholders(message.trim());
    if (/\{first_name\}|\{club\}/i.test(finalMsg)) {
      toast.error('No matched contact to fill {first_name}/{club} — edit the message or check the number');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post(`${API}/analytics/sms/send-direct`, {
        to_phone: phone.trim(), content: finalMsg,
      });
      toast.success(`SMS sent to ${r.data?.yabbr?.to || phone.trim()}`);
      setPhone('');
      setMessage('');
      loadExtras();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SMS send failed');
    }
    setSending(false);
  };

  const saveTemplate = async () => {
    if (!tplName.trim() || !message.trim() || savingTpl) return;
    setSavingTpl(true);
    try {
      await axios.post(`${API}/analytics/sms-templates`, { name: tplName.trim(), content: message.trim() });
      toast.success('Template saved');
      setTplName('');
      setNamingTpl(false);
      loadExtras();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save template');
    }
    setSavingTpl(false);
  };

  const deleteTemplate = async (tpl) => {
    try {
      await axios.delete(`${API}/analytics/sms-templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
      toast.success(`Template "${tpl.name}" removed`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove template');
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
        data-testid="send-sms-open-btn"
        title="Send an SMS to any number via YABBR"
      >
        <MessageSquarePlus className="w-4 h-4 text-teal-600" />
        Send SMS
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="send-sms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <MessageSquarePlus className="w-4 h-4 text-teal-600" /> Send an SMS
            </DialogTitle>
            <DialogDescription className="text-xs">
              Sent via YABBR from your club's number. The send is logged in SMS history.
            </DialogDescription>
          </DialogHeader>
          {!canSend && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800" data-testid="send-sms-no-access-notice">
              SMS sending isn't enabled for your account yet — an admin can switch it on in <strong>Team Users → SMS toggle</strong>. You can still view conversations in the SMS Inbox.
            </div>
          )}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide opacity-60">To (mobile number)</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="04xx xxx xxx or 614xxxxxxxx"
              className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              data-testid="send-sms-phone-input"
            />
            {match && (
              <p className="mt-1 text-[11px] text-teal-700 font-semibold" data-testid="send-sms-contact-match">
                Matched: {match.name || '—'}{match.club ? ` · ${match.club}` : ''}
              </p>
            )}
          </div>
          {templates.length > 0 && (
            <div data-testid="sms-template-list">
              <label className="text-[11px] font-bold uppercase tracking-wide opacity-60">Templates</label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {templates.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1 text-[11px] font-semibold bg-teal-50 text-teal-800 border border-teal-200 rounded-full pl-2.5 pr-1.5 py-0.5">
                    <button onClick={() => setMessage(fillPlaceholders(t.content).slice(0, MAX_LEN))} title={t.content}
                      className="hover:underline" data-testid={`sms-template-use-${t.id}`}>{t.name}</button>
                    <button onClick={() => deleteTemplate(t)} className="opacity-40 hover:opacity-100 font-bold"
                      title="Delete template" data-testid={`sms-template-delete-${t.id}`}>×</button>
                  </span>
                ))}
              </div>
              <p className="text-[10px] opacity-50 mt-1">Templates can use {'{first_name}'} and {'{club}'} — they auto-fill when the number matches a known contact.</p>
            </div>
          )}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide opacity-60">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value.slice(0, MAX_LEN))}
              rows={4}
              placeholder="Type your message…"
              className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 bg-transparent resize-none focus:outline-none focus:ring-2 focus:ring-teal-500/40"
              data-testid="send-sms-message-input"
            />
            <div className="flex items-center justify-between">
              {message.trim() && !namingTpl && (
                <button onClick={() => setNamingTpl(true)}
                  className="flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:underline"
                  data-testid="sms-template-save-open-btn">
                  <BookmarkPlus className="w-3 h-3" /> Save as template
                </button>
              )}
              {namingTpl && (
                <span className="flex items-center gap-1.5">
                  <input value={tplName} onChange={(e) => setTplName(e.target.value.slice(0, 60))}
                    placeholder="Template name…" autoFocus
                    className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-transparent w-36"
                    data-testid="sms-template-name-input" />
                  <button onClick={saveTemplate} disabled={!tplName.trim() || savingTpl}
                    className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-teal-600 text-white disabled:opacity-40"
                    data-testid="sms-template-save-btn">{savingTpl ? '…' : 'Save'}</button>
                  <button onClick={() => { setNamingTpl(false); setTplName(''); }}
                    className="text-[11px] opacity-50 hover:opacity-100">Cancel</button>
                </span>
              )}
              <p className="text-[10px] opacity-50 ml-auto">{message.length}/{MAX_LEN}</p>
            </div>
          </div>
          <button
            onClick={send}
            disabled={sending || !phone.trim() || !message.trim() || !canSend}
            className="w-full flex items-center justify-center gap-2 text-sm font-bold py-2 rounded-full bg-teal-600 text-white hover:bg-teal-500 disabled:opacity-40"
            data-testid="send-sms-submit-btn"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending…' : 'Send SMS'}
          </button>
          <div className="border-t border-slate-100 pt-2">
            <button onClick={() => setShowHistory((v) => !v)}
              className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide opacity-60 hover:opacity-100"
              data-testid="sms-history-toggle-btn">
              <History className="w-3.5 h-3.5" /> Recent sends {history.length ? `(${history.length})` : ''}
            </button>
            {showHistory && (
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1.5" data-testid="sms-history-list">
                {history.map((h) => (
                  <div key={h.id} className="text-[11px] rounded-md bg-slate-50 px-2.5 py-1.5" data-testid={`sms-history-row-${h.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-bold">{h.to_phone}</span>
                      <span className="opacity-50 whitespace-nowrap">{fmtWhen(h.sent_at)} · {h.sender_name}{h.yabbr_simulated ? ' · simulated' : ''}</span>
                    </div>
                    <p className="opacity-70 truncate" title={h.content}>{h.content}</p>
                    {h.reply && (
                      <p className="mt-0.5 text-teal-700 truncate" title={h.reply.content} data-testid={`sms-history-reply-${h.id}`}>
                        <span className="font-bold">↩ Replied {fmtWhen(h.reply.received_at)}:</span> {h.reply.content}
                      </p>
                    )}
                  </div>
                ))}
                {!history.length && <p className="text-[11px] opacity-50 italic">No SMS sent from here yet.</p>}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
